import { Stack } from "@mui/material";
import AcxLoadingIndicator from "components/UI/AcxLoadingIndicator";
import { observer } from "mobx-react";
import { ApplicationFiltersStore } from "stores/ApplicationFilters/ApplicationFiltersStore";
import { useStore } from "utils/useStore";
import { IAppliedFilterChipComponent } from "../BaseChipComponents/IAppliedFilterChipComponent";
import AppliedFilterChipBase from "../BaseChipComponents/AppliedFilterChipBase";
import {
    CHIP_AGGREGATION_THRESHOLD,
    renderCollectionChips,
    useChipFiltersStore,
} from "../BaseChipComponents/chipUtils";

export const HierarchiesChips = observer(function (
    props: IAppliedFilterChipComponent,
) {
    const globalApplicationFiltersStore = useStore(ApplicationFiltersStore);
    const applicationFiltersStore = useChipFiltersStore(props);
    const chipStore = props.store
        ? applicationFiltersStore
        : globalApplicationFiltersStore.quickFiltersStore;

    const hierarchyIds = chipStore.hierarchyIds;
    const hierarchyLabels = chipStore.hierarchyLabels;
    const hierarchyCount = hierarchyIds.length;
    const hasMissingLabels = hierarchyIds.some((id) => !hierarchyLabels[id]);
    const isHierarchyLoading =
        chipStore.isHierarchyLabelsLoading && hasMissingLabels;

    const hierarchyChipLoadingIndicator = (
        <Stack
            direction="row"
            flexWrap="nowrap"
            alignItems="center"
            spacing={1}
        >
            <span>Hierarchy:</span>
            <AcxLoadingIndicator size={16} alternate="PuffLoader" />
        </Stack>
    );

    if (isHierarchyLoading) {
        if (hierarchyCount > CHIP_AGGREGATION_THRESHOLD) {
            return (
                <AppliedFilterChipBase
                    disabled={props.disabled}
                    isApplicable={props.isApplicable}
                >
                    {hierarchyChipLoadingIndicator}
                </AppliedFilterChipBase>
            );
        }

        return hierarchyIds.map((id) => (
            <AppliedFilterChipBase
                key={id}
                disabled={props.disabled}
                isApplicable={props.isApplicable}
            >
                {hierarchyChipLoadingIndicator}
            </AppliedFilterChipBase>
        ));
    }

    return renderCollectionChips({
        items: hierarchyIds,
        disabled: props.disabled,
        isApplicable: props.isApplicable,
        getKey: (id) => id,
        getLabel: (id) => `Hierarchy: ${hierarchyLabels[id] ?? id}`,
        getTooltipLabel: (id) => hierarchyLabels[id] ?? id,
        getAggregatedLabel: (count) => `${count} Hierarchies`,
        onRemoveAll: () => {
            chipStore.setHierarchyIds([]);
            if (!props.store) {
                applicationFiltersStore.setHierarchyIds([]);
            }
        },
        onRemoveItem: (id) => {
            chipStore.removeHierarchyId(id);
            if (!props.store) {
                applicationFiltersStore.removeHierarchyId(id);
            }
        },
    });
});
